import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { LightboxModal } from '../components/LightboxModal';
import { ContactForm } from '../components/ContactForm';
import { SubpageHero } from '../components/SubpageHero';
import { SERVICES, COMPANY, DEFAULT_GALLERY } from '../data/company';
import { useCmsContent } from '../hooks/useCmsData';
import { getCdnImageUrl } from '../lib/utils';

export function ServiceDetailPage() {
  const { serviceSlug } = useParams();
  const { items: cmsServices } = useCmsContent('ydelser');
  const { items: cmsGallery } = useCmsContent('galleri', DEFAULT_GALLERY);

  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  // Find match in CMS services or static SERVICES array
  const matchedService = (cmsServices && cmsServices.find(s => (s.service_slug || s.slug?._ || s.slug) === serviceSlug)) 
    || SERVICES.find(s => s.slug === serviceSlug);

  if (!matchedService) {
    return <Navigate to="/ydelser" replace />;
  }

  const title = matchedService.title;
  const iconClass = matchedService.icon || "fa-solid fa-wrench";
  const shortDesc = matchedService.short_description || matchedService.shortDescription;
  const fullDesc = matchedService.full_description || matchedService.description;
  const mainImg = Array.isArray(matchedService.main_image) ? matchedService.main_image[0] : (matchedService.main_image || matchedService.image);
  
  // Gallery category mapping
  const galleryCatSlug = matchedService.gallery_slug || matchedService.gallerySlug || serviceSlug;

  const gallerySource = (cmsGallery && cmsGallery.length > 0) ? cmsGallery : DEFAULT_GALLERY;

  // Filter and expand gallery items from CMS matching this service's category_slug
  const rawGalleryItems = gallerySource ? gallerySource.filter(item => item.category_slug === galleryCatSlug) : [];
  const relatedGalleryItems = rawGalleryItems.flatMap((item, itemIdx) => {
    const images = Array.isArray(item.image) ? item.image : (item.image ? [item.image] : []);
    return images.map((imgUrl, imgIdx) => ({
      ...item,
      id: `${item.id || itemIdx}-${imgIdx}`,
      image: imgUrl,
    }));
  });

  // SEO Description customization
  const seoDescription = `${title} i Juelsminde: ${shortDesc} Kontakt Altmuligmanden Juelsminde for et uforpligtende tilbud.`;

  return (
    <>
      <SEO 
        title={title} 
        description={seoDescription} 
      />

      {/* Hero Header */}
      <SubpageHero
        badge="Ydelse i Juelsminde & Omegn"
        title={title}
        subtitle={shortDesc}
        icon={iconClass}
        breadcrumbs={[
          { label: "Ydelser", link: "/ydelser" },
          { label: title }
        ]}
      />

      {/* Main Content Area */}
      <section className="py-16 bg-sand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-16">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Description & Details */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white p-8 rounded-2xl border border-sand-border shadow-sm space-y-6">
                <h2 className="text-2xl font-bold text-forest border-b border-sand-border pb-4">
                  Om {title} hos Altmuligmanden Juelsminde
                </h2>

                {/* Render RichText HTML or plain string safely */}
                {typeof fullDesc === 'string' && fullDesc.includes('<p>') ? (
                  <div 
                    className="prose prose-forest max-w-none text-charcoal leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: fullDesc }}
                  />
                ) : (
                  <p className="text-charcoal leading-relaxed text-base sm:text-lg">
                    {fullDesc}
                  </p>
                )}



                <div className="pt-4 border-t border-sand-border bg-sand-light/60 p-5 rounded-xl space-y-3">
                  <h3 className="font-bold text-forest text-sm uppercase tracking-wider flex items-center gap-2">
                    <i className="fa-solid fa-thumbs-up text-copper" aria-hidden="true" />
                    <span>Hvorfor vælge mig til denne opgave?</span>
                  </h3>
                  <ul className="text-xs sm:text-sm text-muted space-y-2">
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-forest" aria-hidden="true" />
                      <span>Lokal og fleksibel – hurtig udrykning i Juelsminde og omegn</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-forest" aria-hidden="true" />
                      <span>Grundigt forarbejde og pæn oprydning efter endt arbejde</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="fa-solid fa-check text-forest" aria-hidden="true" />
                      <span>Gennemskuelige aftaler uden ubehagelige overraskelser</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Service Navigation Back Link */}
              <div className="pt-2">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-2 font-bold text-forest hover:text-copper transition-colors cursor-pointer text-sm"
                >
                  <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                  <span>Tilbage til alle ydelser</span>
                </Link>
              </div>
            </div>

            {/* Right Column: Contact Box */}
            <div className="lg:col-span-5 space-y-8">
              <ContactForm title={`Få et tilbud på ${title}`} />
            </div>

          </div>

          {/* CMS Gallery Section Connected via category_slug */}
          {relatedGalleryItems.length > 0 && (
            <div className="pt-10 border-t border-sand-border space-y-6">
              <div className="text-center max-w-2xl mx-auto space-y-2">
                <span className="text-copper font-bold text-xs uppercase tracking-widest bg-copper-light px-3 py-1 rounded-full">
                  Projektgalleri
                </span>
                <h3 className="text-2xl font-bold text-forest">
                  Billeder af udført {title.toLowerCase()}
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedGalleryItems.map((gItem, idx) => {
                  const gImg = Array.isArray(gItem.image) ? gItem.image[0] : gItem.image;
                  if (!gImg) return null;

                  return (
                    <div
                      key={gItem.id || idx}
                      onClick={() => setLightboxState({ isOpen: true, index: idx })}
                      className="group relative bg-white rounded-xl overflow-hidden border border-sand-border shadow-xs hover:shadow-lg transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-4/3 overflow-hidden bg-sand">
                        <img
                          src={getCdnImageUrl(gImg, 600)}
                          alt={gItem.title || title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 bg-white flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-forest text-sm group-hover:text-copper transition-colors">
                            {gItem.title || title}
                          </h4>
                          {gItem.description && (
                            <p className="text-xs text-muted truncate">{gItem.description}</p>
                          )}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-sand flex items-center justify-center text-forest group-hover:bg-copper group-hover:text-white transition-colors text-xs shrink-0">
                          <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Lightbox Modal for gallery items */}
      {relatedGalleryItems.length > 0 && (
        <LightboxModal
          isOpen={lightboxState.isOpen}
          image={
            Array.isArray(relatedGalleryItems[lightboxState.index]?.image)
              ? relatedGalleryItems[lightboxState.index]?.image[0]
              : relatedGalleryItems[lightboxState.index]?.image
          }
          title={relatedGalleryItems[lightboxState.index]?.title}
          description={relatedGalleryItems[lightboxState.index]?.description}
          onClose={() => setLightboxState({ isOpen: false, index: 0 })}
          onPrev={
            relatedGalleryItems.length > 1
              ? () =>
                  setLightboxState((prev) => ({
                    ...prev,
                    index: (prev.index - 1 + relatedGalleryItems.length) % relatedGalleryItems.length,
                  }))
              : null
          }
          onNext={
            relatedGalleryItems.length > 1
              ? () =>
                  setLightboxState((prev) => ({
                    ...prev,
                    index: (prev.index + 1) % relatedGalleryItems.length,
                  }))
              : null
          }
        />
      )}
    </>
  );
}
